import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import PageHeader from '../../components/settings/PageHeader';
import SettingCard from '../../components/settings/SettingCard';
import { useAuth } from '../../hooks/useAuth';
import { settingsService } from '../../services/settings/settings.service';
import { friendlyAuthError, needsReauth } from '../../utils/authErrors';

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400 transition-all text-sm';

const CONSEQUENCES = [
  'Your profile, messages and activity will be permanently removed.',
  'Wallet balances and pending payments cannot be recovered.',
  'You will lose access to any businesses and subscriptions.',
  'This action cannot be undone.',
];

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { user, reauthenticate, deleteAccount } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canDelete = confirmText.trim().toUpperCase() === 'DELETE' && password.length > 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canDelete) return;
    setError(null);
    setSubmitting(true);
    try {
      // Re-authenticate first — Firebase requires a recent login to delete.
      await reauthenticate(password);
      await deleteAccount();
      settingsService.clearLocal();
      navigate('/auth/register', { replace: true });
    } catch (e2) {
      setError(needsReauth(e2) ? 'Please re-enter your password and try again.' : friendlyAuthError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <PageHeader title="Delete account" subtitle="Permanently remove your Messego account" icon={Trash2} backTo="/app/settings/account" />

      <div className="flex items-start gap-3 rounded-3xl border border-rose-200 bg-rose-50 dark:bg-rose-950/40 px-5 py-4">
        <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-rose-700 text-sm">This is permanent</p>
          <ul className="mt-2 space-y-1">
            {CONSEQUENCES.map((c) => (
              <li key={c} className="text-xs text-rose-700/90 flex gap-2">
                <span className="text-rose-400">•</span>
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <SettingCard>
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <label className="text-sm font-medium text-neutral-700">Confirm your password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass + ' mt-1'} autoComplete="current-password" placeholder="••••••••" />
            {user?.email && <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">Signed in as {user.email}</p>}
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-700">Type <span className="font-mono font-bold text-rose-600 dark:text-rose-400">DELETE</span> to confirm</label>
            <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className={inputClass + ' mt-1'} placeholder="DELETE" />
          </div>

          {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={!canDelete || submitting}
            className="w-full bg-rose-600 text-white py-3 rounded-xl font-semibold hover:bg-rose-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Deleting account…' : 'Permanently delete account'}
          </button>
          <button type="button" onClick={() => navigate('/app/settings/account')} className="w-full py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 font-semibold text-sm hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
            Cancel
          </button>
        </form>
      </SettingCard>
    </div>
  );
}
